/**
 * Transform image URLs to Cloudinary optimized URLs using fetch delivery
 * @param {string} originalUrl - Original image URL (Vercel Blob or other)
 * @param {Object} options - Cloudinary transformation options
 * @param {number} options.width - Desired width in pixels
 * @param {number} options.height - Desired height in pixels
 * @param {string} options.crop - Crop mode (scale, fill, fit, etc.)
 * @param {string} options.quality - Image quality (auto, 80, 90, etc.)
 * @param {string} options.format - Output format (auto, webp, avif, jpg)
 * @returns {string} - Optimized Cloudinary URL or original if Cloudinary not configured
 */
export function getCloudinaryUrl(originalUrl, options = {}) {
  if (!originalUrl) return null;
  
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  
  // If Cloudinary is not configured, return original URL
  if (!cloudName) {
    return originalUrl;
  }
  
  // If URL is already a Cloudinary URL, return as-is
  if (originalUrl.includes('res.cloudinary.com')) {
    return originalUrl;
  }
  
  // Build Cloudinary transformation parameters
  const transformations = [];
  
  if (options.width) {
    transformations.push(`w_${options.width}`);
  }
  
  if (options.height) {
    transformations.push(`h_${options.height}`);
  }
  
  if (options.crop) {
    transformations.push(`c_${options.crop}`);
  }
  
  if (options.quality) {
    transformations.push(`q_${options.quality}`);
  } else {
    // Default to auto quality for best performance
    transformations.push('q_auto');
  }
  
  if (options.format) {
    transformations.push(`f_${options.format}`);
  } else {
    // Auto format selection (WebP/AVIF when supported)
    transformations.push('f_auto');
  }
  
  const transformString = transformations.join(',');
  
  // For external URLs (like Vercel Blob), use Cloudinary's fetch delivery type
  // Format: https://res.cloudinary.com/<cloud_name>/image/fetch/<transformations>/<external_url>
  // IMPORTANT: The source URL must be URL-encoded for Cloudinary fetch to work properly
  if (originalUrl.startsWith('http://') || originalUrl.startsWith('https://')) {
    const encodedUrl = encodeURIComponent(originalUrl);
    return `https://res.cloudinary.com/${cloudName}/image/fetch/${transformString}/${encodedUrl}`;
  }
  
  // For relative paths (if images were uploaded to Cloudinary)
  let imagePath = originalUrl;
  if (!originalUrl.startsWith('/')) {
    imagePath = '/' + originalUrl;
  }
  
  return `https://res.cloudinary.com/${cloudName}/image/upload/${transformString}${imagePath}`;
}

/**
 * Transform video URLs to Cloudinary optimized URLs using video/fetch delivery.
 * Cloudinary will transcode and cache the video on its CDN, serving the best
 * format (MP4/WebM) and quality for each browser automatically.
 * @param {string} originalUrl - Original video URL (Vercel Blob or other)
 * @param {Object} options - Cloudinary transformation options
 * @param {number} options.width - Desired width in pixels
 * @param {string} options.quality - Video quality (auto, 80, 90, etc.)
 * @param {string} options.format - Output format (auto, mp4, webm)
 * @returns {string} - Optimized Cloudinary URL or original if Cloudinary not configured
 */
export function getCloudinaryVideoUrl(originalUrl, options = {}) {
  if (!originalUrl) return null;

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

  if (!cloudName) {
    return originalUrl;
  }

  if (originalUrl.includes('res.cloudinary.com')) {
    return originalUrl;
  }

  const transformations = [];

  if (options.width) {
    transformations.push(`w_${options.width}`);
  }

  if (options.quality) {
    transformations.push(`q_${options.quality}`);
  } else {
    transformations.push('q_auto');
  }

  if (options.format) {
    transformations.push(`f_${options.format}`);
  } else {
    transformations.push('f_auto');
  }

  const transformString = transformations.join(',');

  if (originalUrl.startsWith('http://') || originalUrl.startsWith('https://')) {
    const encodedUrl = encodeURIComponent(originalUrl);
    return `https://res.cloudinary.com/${cloudName}/video/fetch/${transformString}/${encodedUrl}`;
  }

  let videoPath = originalUrl;
  if (!originalUrl.startsWith('/')) {
    videoPath = '/' + originalUrl;
  }

  return `https://res.cloudinary.com/${cloudName}/video/upload/${transformString}${videoPath}`;
}

/**
 * Get optimized avatar URL (64x64px, cropped to fill, auto format, auto quality)
 * @param {string} originalUrl - Original avatar URL
 * @returns {string} - Optimized Cloudinary URL or original if unavailable
 */
export function getOptimizedAvatarUrl(originalUrl) {
  if (!originalUrl) return null;
  
  return getCloudinaryUrl(originalUrl, {
    width: 64, // 2x for retina displays (displayed at 32px)
    height: 64,
    crop: 'fill', // Scale and crop to fill the dimensions
    quality: 'auto',
    format: 'auto'
  });
}

/**
 * Extract the underlying raw URL from a Cloudinary fetch URL, or strip
 * transformation parameters from a Cloudinary upload URL if transformations fail.
 * @param {string} url
 * @returns {string|null}
 */
export function extractOriginalImageUrl(url) {
  if (!url || typeof url !== 'string') return null;

  // 1) Cloudinary fetch URL: https://res.cloudinary.com/<cloud>/<type>/fetch/<transforms>/<encodedUrl>
  const fetchMatch = url.match(/res\.cloudinary\.com\/[^/]+\/(?:image|video)\/fetch\/[^/]+\/(.+)$/);
  if (fetchMatch && fetchMatch[1]) {
    try {
      const decoded = decodeURIComponent(fetchMatch[1]);
      if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
        return decoded;
      }
    } catch (_) {
      if (fetchMatch[1].startsWith('http://') || fetchMatch[1].startsWith('https://')) {
        return fetchMatch[1];
      }
    }
  }

  // 2) Cloudinary upload URL with transformations:
  //    https://res.cloudinary.com/<cloud>/image/upload/<transforms>/v12345/path.jpg
  const uploadMatch = url.match(/^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)([^/]+\/)(v\d+\/.+)$/);
  if (uploadMatch) {
    return `${uploadMatch[1]}${uploadMatch[3]}`;
  }

  return null;
}

/**
 * Universal onError handler for <img> elements that may be using Cloudinary URLs
 * or external URLs. Retries the raw origin URL first (if wrapped in Cloudinary),
 * then falls back to a safe local asset so broken image icons never appear.
 */
export function handleCloudinaryImageError(event, fallbackUrl = '/house.png') {
  const img = event?.currentTarget || event?.target;
  if (!img || img.tagName !== 'IMG') return;

  const currentSrc = img.getAttribute('src') || img.src || '';
  const rawUrl = extractOriginalImageUrl(currentSrc);

  if (rawUrl && rawUrl !== currentSrc && !img.dataset.cldRawRetried) {
    img.dataset.cldRawRetried = '1';
    img.src = rawUrl;
    return;
  }

  if (fallbackUrl && currentSrc !== fallbackUrl && !img.dataset.cldFallbackRetried) {
    img.dataset.cldFallbackRetried = '1';
    img.src = fallbackUrl;
  }
}

// Install a global capture-phase image error listener in the browser so that
// if Cloudinary ever hits a billing/quota limit or rejects a fetch anywhere in
// the app, every <img> automatically retries the underlying raw URL (e.g. Vercel Blob),
// and property images never render a broken-image icon.
if (typeof window !== 'undefined' && !window.__postawayImageErrorFallbackInstalled) {
  window.__postawayImageErrorFallbackInstalled = true;
  window.addEventListener(
    'error',
    (event) => {
      const target = event?.target;
      if (!target || target.tagName !== 'IMG') return;

      const currentSrc = target.getAttribute('src') || target.src || '';
      const rawUrl = extractOriginalImageUrl(currentSrc);

      if (rawUrl && rawUrl !== currentSrc && !target.dataset.cldRawRetried) {
        target.dataset.cldRawRetried = '1';
        target.src = rawUrl;
        return;
      }

      const alt = (target.getAttribute('alt') || '').toLowerCase();
      const isPropertyImage =
        alt === 'property' ||
        alt.includes('property') ||
        currentSrc.includes('1566073771259-d747097ecc71');

      if (isPropertyImage && !target.dataset.cldFallbackRetried && !currentSrc.endsWith('/house.png')) {
        target.dataset.cldFallbackRetried = '1';
        target.src = '/house.png';
      }
    },
    true
  );
}


